import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectParsedConfigurationItems,
  selectDependencyByType,
} from '../../../../reducers/documentation/documentationReducer';
import { ConfigurationItemMap } from '../../configuration-item-tables/shared/helpers';
import { generateFieldId, ID_NAME_SEPARATOR } from '../../universal-search/utils';
import { ConfigurationType } from '../types';
import { checkIfActive, createConfigurationItemFromParsedRule } from '../../helpers';
import { selectParsedRules } from '../../../../reducers/global/globalReducer';
import { ConfigurationItem } from '../DependenciesTypes';

interface UseGetParsedDependencies {
  configurationItemId: string;
  crmOrgId: string;
  type: 'dependsOnIdsForRules' | 'usedByIdsForRules' | 'usedByIdsForFields';
  name: string;
  objectName: string;
  onlyActive: boolean;
}

export const useGetParsedDependencies = ({
  configurationItemId,
  crmOrgId,
  type,
  name,
  objectName,
  onlyActive,
}: UseGetParsedDependencies) => {
  const _fieldId = generateFieldId({ id: configurationItemId, name, objectName });
  const configs = useSelector(selectParsedConfigurationItems(crmOrgId));
  const parsedRules = useSelector(selectParsedRules(crmOrgId));

  const id = type === 'usedByIdsForFields' ? _fieldId : (configurationItemId ?? '');
  const rulesIds = useSelector(selectDependencyByType(type))?.[crmOrgId]?.[id];

  return useMemo(() => {
    if (!rulesIds) {
      return {} as ConfigurationItemMap;
    }

    const newDep = {} as ConfigurationItemMap;

    // Create lookup maps to avoid repeated findIndex calls
    const rulesLookup = new Map<string, any>();
    parsedRules?.forEach((rule) => {
      const key = `${rule.id}_${rule.name || ''}`;
      rulesLookup.set(key, rule);
    });

    const configsLookup = new Map<string, any>();
    configs?.forEach((config) => {
      const key = `${config.id}_${config.name || ''}`;
      configsLookup.set(key, config);
    });

    Object.keys(rulesIds).forEach((key) => {
      const configKey = key as ConfigurationType;
      const items = rulesIds[configKey];
      const newConfigItems: ConfigurationItem[] = [];

      items.forEach((id) => {
        const [_id, _name] = id.split(ID_NAME_SEPARATOR);
        const lookupKey = `${_id}_${_name || ''}`;

        const rule = rulesLookup.get(lookupKey);
        if (rule) {
          if (checkIfActive(onlyActive, rule)) {
            newConfigItems.push(createConfigurationItemFromParsedRule(rule));
          }
          return;
        }

        let config = configsLookup.get(lookupKey);

        if (!config && _id) {
          const idOnlyKey = `${_id}_`;
          config = configsLookup.get(idOnlyKey);

          if (!config) {
            for (const [key, value] of configsLookup.entries()) {
              const [storedId] = key.split('_');
              if (storedId === _id) {
                config = value;

                break;
              }
            }
          }
        }

        if (config) {
          if (checkIfActive(onlyActive, config)) {
            newConfigItems.push(config);
          }
        }
        return;
      });

      newDep[configKey] = newConfigItems;
    });

    return newDep;
  }, [configs, parsedRules, rulesIds, onlyActive]);
};
